import { useQuery } from "@apollo/client"
import { type NodeOption } from "@skyltmax/ds9"
import { type Extensions } from "@tiptap/core"
import Document from "@tiptap/extension-document"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"
import cn from "classnames"
import React from "react"
import { graphql } from "@/api"
import StaticTiptapDisplay from "@/client/components/static_tiptap_display"
import { createCustomParagraph } from "@/common/tiptap_stack"
import { isBlank } from "@/common/util"

export const interstitialDoc = graphql(`
  query ExampleInterstitial($id: ID!) {
    exampleInterstitial(id: $id) {
      id
      locale
      business
      heading
      description
      createdAt
      textDark
      buttonLabel
      buttonUrl
      version
      backgroundColor

      admin {
        name
        avatarSmallUrl
      }

      image {
        width
        height
        data

        pngOnex
        pngTwox
        webpOnex
        webpTwox
      }

      ...ExampleInterstitialsProductPages_ExampleInterstitial
    }
  }
`)

type ParagraphStyle =
  | "paragraph"
  | "paragraph-sm"
  | "paragraph-xs"
  | "paragraph-2xs"
  | "heading-one"
  | "heading-two"
  | "heading-three"
  | "heading-four"
  | "heading-five"
  | "heading-six"

const paragraphStyles: ParagraphStyle[] = [
  "paragraph",
  "paragraph-sm",
  "paragraph-xs",
  "paragraph-2xs",
  "heading-one",
  "heading-two",
  "heading-three",
  "heading-four",
  "heading-five",
  "heading-six",
]

export const interstitialContentNodes: NodeOption<ParagraphStyle>[] = [
  { node: "paragraph", param: "paragraph", label: "Paragraph" },
  { node: "paragraph", param: "paragraph-sm", label: "Paragraph small" },
  { node: "paragraph", param: "paragraph-xs", label: "Paragraph xs" },
  { node: "paragraph", param: "paragraph-2xs", label: "Paragraph 2xs" },
  { node: "paragraph", param: "heading-one", label: "Heading 1" },
  { node: "paragraph", param: "heading-two", label: "Heading 2" },
  { node: "paragraph", param: "heading-three", label: "Heading 3" },
  { node: "paragraph", param: "heading-four", label: "Heading 4" },
  { node: "paragraph", param: "heading-five", label: "Heading 5" },
  { node: "paragraph", param: "heading-six", label: "Heading 6" },
]

export const paragraphStack: Extensions = [
  StarterKit.configure({
    document: false,
    paragraph: false,
  }),
  createCustomParagraph(paragraphStyles),
  Document,
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  TextAlign.configure({ types: ["paragraph", "heading"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

const getContrastYIQ = (hexColor: string) => {
  hexColor = hexColor.replace(/^#/, "")

  const r = parseInt(hexColor.slice(0, 2), 16)
  const g = parseInt(hexColor.slice(2, 4), 16)
  const b = parseInt(hexColor.slice(4, 6), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000

  return yiq >= 128 ? "light" : "dark"
}

interface ExampleInterstitialCardProps {
  id?: string
  heading?: string | null
  description?: any
  image?: {
    pngOnex: string
    pngTwox: string
    webpOnex: string
    webpTwox: string
  } | null
  version?: number
  textDark?: boolean
  buttonLabel?: string
  buttonUrl?: string
  backgroundColor?: string
}

const ExampleInterstitialCard: React.FC<ExampleInterstitialCardProps> = ({
  id,
  heading,
  description,
  image,
  version,
  textDark,
  buttonLabel,
  buttonUrl,
  backgroundColor,
}) => {
  const results = useQuery(interstitialDoc, {
    variables: { id: id || "" },
    skip: !id,
  })
  const interstitial = results.data?.exampleInterstitial

  const cardHeading = heading || interstitial?.heading
  const cardDescription = description || interstitial?.description
  const cardImage = image || interstitial?.image
  const cardVersion = version || interstitial?.version || 1
  const cardTextDark =
    textDark !== undefined || interstitial?.textDark !== undefined ? textDark || interstitial?.textDark : true
  const cardButtonLabel = buttonLabel || interstitial?.buttonLabel || ""
  const cardButtonUrl = buttonUrl || interstitial?.buttonUrl || ""
  const cardBackgroundColor = backgroundColor || interstitial?.backgroundColor || "#cce1fd"

  const backgroundDark = getContrastYIQ(cardBackgroundColor) === "dark"
  const textLight = !cardImage || (cardImage && cardVersion === 1) ? backgroundDark : cardVersion === 2 && !cardTextDark
  const paragraphClass = textLight ? "[&_*]:text-white-3" : "[&_*]:text-ink-100"

  return (
    <div
      className="relative flex max-h-[404px] min-h-[292px] flex-1 flex-col overflow-hidden rounded-lg lg:max-h-[450px] lg:min-h-[404px]"
      style={{ backgroundColor: cardBackgroundColor }}
    >
      {cardVersion === 1 && (
        <div className="px-2 py-4 lg:px-4 lg:py-6">
          <h3 className={cn("heading-five sm:heading-four mb-0 lg:mb-1", { "!text-white-3": textLight })}>
            {cardHeading}
          </h3>

          {cardDescription && (
            <StaticTiptapDisplay className={paragraphClass} extensions={paragraphStack} json={cardDescription} />
          )}
        </div>
      )}

      <div
        className={cn("relative flex-1", {
          "lg:pt-2": cardVersion === 1,
          "lg:pt-0": cardVersion === 2,
        })}
      >
        {cardImage && (
          <picture>
            <source srcSet={`${cardImage.webpOnex}, ${cardImage.webpTwox} 2x`} type="image/webp" />
            <img
              srcSet={`${cardImage.pngOnex}, ${cardImage.pngTwox} 2x`}
              className={cn("h-full w-full object-cover object-top", {
                "insent-0 absolute": cardVersion === 2,
              })}
              loading="lazy"
            />
          </picture>
        )}
        {cardVersion === 2 && (
          <>
            <div
              className={cn("absolute h-full w-full", {
                "bg-white opacity-25": cardImage && cardTextDark,
                "bg-black opacity-25": cardImage && !cardTextDark,
              })}
            ></div>
            <div className="relative mx-2 mt-4 flex h-[180px] max-h-[180px] flex-col justify-end overflow-hidden sm:h-[260px] sm:max-h-[260px] lg:mx-4 lg:mt-6 lg:h-[220px] lg:max-h-[220px]">
              <h3
                className={cn("heading-five sm:heading-four mb-0 !text-center lg:mb-1", { "!text-white-3": textLight })}
              >
                {cardHeading}
              </h3>

              {cardDescription && (
                <StaticTiptapDisplay className={paragraphClass} extensions={paragraphStack} json={cardDescription} />
              )}
            </div>

            {!isBlank(cardButtonLabel) && (
              <div className="mb-10 flex w-full justify-center">
                <a
                  className={cn("button lg:button-large relative mb-0 mt-4 lg:mt-8", {
                    "button-primary": cardTextDark,
                    "button-emphasis": !cardTextDark,
                  })}
                  href={cardButtonUrl}
                >
                  {cardButtonLabel}
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ExampleInterstitialCard
